<script setup lang="ts">
import { StorePublicTicketRequest } from '@libero/api-client/public-ticket/types';
import { useFormContext } from '@libero/hooks/useForm';
import Button from '@libero/ui-framework/Button/Button.vue';
import Checkbox from '@libero/ui-framework/Checkbox/Checkbox.vue';
import Cluster from '@libero/ui-framework/Cluster/Cluster.vue';
import FormLayout from '@libero/ui-framework/FormLayout/FormLayout.vue';
import Input from '@libero/ui-framework/Input/Input.vue';
import InputGroup from '@libero/ui-framework/InputGroup/InputGroup.vue';
import Label from '@libero/ui-framework/Label/Label.vue';
import Stack from '@libero/ui-framework/Stack/Stack.vue';
import SubLabel from '@libero/ui-framework/SubLabel/SubLabel.vue';
import Typography from '@libero/ui-framework/Typography/Typography.vue';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  isLoading: boolean;
  onPreviousStep: () => void;
}

defineProps<Props>();

const { t } = useI18n();

const form = useFormContext<StorePublicTicketRequest>();

const isDisabled = computed(
  () =>
    (form.values.reporter.has_email && !form.values.reporter.email) ||
    (!!form.values.reporter.email && !form.values.reporter.accept_terms) ||
    (!!form.values.reporter.phone && !form.values.reporter.accept_terms),
);

watch(form.values, () => {
  if (!form.values.reporter.has_email) {
    form.clearValues(['reporter.email']);
  }
});
</script>

<template>
  <Typography tag="h1" type="heading" size="xl" bold>
    {{ t('citizen-report.reporter-title') }}
  </Typography>

  <Typography size="lg">
    {{ t('citizen-report.reporter-description') }}
  </Typography>

  <Stack :gap="2">
    <FormLayout>
      <InputGroup>
        <Checkbox size="lg" v-bind="form.register('reporter.has_email')">
          {{ t('citizen-report.has-email') }}
        </Checkbox>
      </InputGroup>

      <InputGroup v-if="form.values.reporter.has_email">
        <Label for="reporter.email" :value="t('citizen-report.email')" size="xl" required />
        <SubLabel size="lg">{{ t('citizen-report.email-note') }}</SubLabel>
        <Input size="lg" v-bind="form.register('reporter.email')" autocomplete="email" />
      </InputGroup>

      <InputGroup>
        <Label for="reporter.phone" :value="t('citizen-report.phone')" size="xl" />
        <SubLabel size="lg">{{ t('citizen-report.phone-note') }}</SubLabel>
        <Input size="lg" v-bind="form.register('reporter.phone')" autocomplete="tel" />
      </InputGroup>

      <InputGroup v-if="form.values.reporter.has_email || form.values.reporter.phone">
        <Checkbox size="lg" v-bind="form.register('reporter.accept_terms')">
          {{ t('citizen-report.accept-terms') }}
        </Checkbox>
      </InputGroup>
    </FormLayout>

    <Cluster justifyContent="space-between">
      <Button size="lg" appearance="outline" :onClick="onPreviousStep">
        {{ t('previous') }}
      </Button>

      <Button size="lg" type="submit" :isLoading="isLoading" :isDisabled="isDisabled">
        {{ t('citizen-report.send') }}
      </Button>
    </Cluster>
  </Stack>
</template>
